import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import {
  ArrowBack,
  Security,
  SwapHoriz,
  Delete,
  Edit,
  Warning,
  Search,
  Person,
  AccountBalance,
  Flag,
  AdminPanelSettings,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import TransferDialog from './TransferDialog';
import CharacterAvatar from './CharacterAvatar';
import { getApiUrl } from '../config';
import { useUGC } from '../contexts/UGCContext';
import { GlassInfoCard, GlassWarningCard } from './ui/GlassComponents';

const AdminPanel = ({ authData, sessionData, onBack }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [transferDialog, setTransferDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [officerDialog, setOfficerDialog] = useState(false);
  const [officerManagementDialog, setOfficerManagementDialog] = useState(false);
  const [markDeceasedDialog, setMarkDeceasedDialog] = useState(false);
  const [selectedEntityForOfficers, setSelectedEntityForOfficers] = useState(null);
  const [entityOfficers, setEntityOfficers] = useState([]);
  const [entityMembers, setEntityMembers] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Entity management state
  const [managementTab, setManagementTab] = useState(0); // 0: Characters, 1: Banks, 2: Nations
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [actionType, setActionType] = useState(''); // 'edit' or 'delete'
  
  // Edit entity state
  const [editEntityData, setEditEntityData] = useState({});
  
  // Officer management state
  // eslint-disable-next-line no-unused-vars
  const [officerAction, setOfficerAction] = useState(''); // 'promote' or 'demote'
  const [officerCharacterName, setOfficerCharacterName] = useState('');
  const [officerEntityType, setOfficerEntityType] = useState('bank'); // 'bank' or 'nation'
  const [officerEntityId, setOfficerEntityId] = useState('');
  
  // Entity lists
  const [characters, setCharacters] = useState([]);
  const [banks, setBanks] = useState([]);
  const [nations, setNations] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);

  // UGC management
  const { ugcEnabled, updateUgcStatus, getImageUrl } = useUGC();
  const [ugcLoading, setUgcLoading] = useState(false);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setError('');
    setSuccess('');
  };

  const handleManagementTabChange = (event, newValue) => {
    setManagementTab(newValue);
    setSearchTerm('');
    setSelectedEntity(null);
    setError('');
    setSuccess('');
  };

  const fetchCharacters = async () => {
    try {
      const response = await fetch(getApiUrl('/admin/entities/characters'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch characters');
      }
      
      const data = await response.json();
      setCharacters(data.characters || []);
    } catch (err) {
      console.error('Failed to load characters:', err);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await fetch(getApiUrl('/admin/entities/banks'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch banks');
      }
      
      const data = await response.json();
      setBanks(data.banks || []);
    } catch (err) {
      console.error('Failed to load banks:', err);
    }
  };

  const fetchNations = async () => {
    try {
      const response = await fetch(getApiUrl('/admin/entities/nations'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch nations');
      }
      
      const data = await response.json();
      setNations(data.nations || []);
    } catch (err) {
      console.error('Failed to load nations:', err);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await fetch(getApiUrl('/currencies'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch currencies');
      }
      
      const data = await response.json();
      setCurrencies(data.currencies || []);
    } catch (err) {
      console.error('Failed to load currencies:', err);
    }
  };

  const toggleUgc = async (enabled) => {
    setUgcLoading(true);
    try {
      const response = await fetch(getApiUrl('/admin/toggle-ugc'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ugcEnabled: enabled }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to toggle UGC');
      }
      
      const data = await response.json();
      updateUgcStatus(data.ugcEnabled); // Update global UGC context
      setSuccess(data.message);
    } catch (err) {
      setError(`Failed to toggle UGC: ${err.message}`);
    } finally {
      setUgcLoading(false);
    }
  };

  const fetchAllEntities = async () => {
    setEntitiesLoading(true);
    try {
      await Promise.all([
        fetchCharacters(),
        fetchBanks(),
        fetchNations(),
        fetchCurrencies()
      ]);
    } catch (err) {
      console.error('Failed to load entities:', err);
    } finally {
      setEntitiesLoading(false);
    }
  };

  useEffect(() => {
    fetchAllEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdminTransfer = async (transferData) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(getApiUrl('/admin/transfer'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          currencySymbol: transferData.currencySymbol,
          amount: parseInt(transferData.amount),
          fromType: transferData.fromType,
          fromId: transferData.fromId,
          toType: transferData.toType,
          toId: transferData.toId,
          description: transferData.description,
          asCharacter: transferData.initiatedBy
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Admin transfer failed');
      }

      const result = await response.json();
      setSuccess(`Admin transfer completed: ${result.transfer.amount} ${result.transfer.currencySymbol}`);
      setTransferDialog(false);
    } catch (err) {
      setError('Admin transfer failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntity = async () => {
    if (!selectedEntity) {
      setError('Please select an entity to delete');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const entityType = managementTab === 0 ? 'character' : managementTab === 1 ? 'bank' : 'nation';
      const response = await fetch(getApiUrl('/admin/entity'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          entityType: entityType,
          entityId: selectedEntity.id
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Delete failed');
      }

      const result = await response.json();
      setSuccess(result.message);
      setDeleteDialog(false);
      setSelectedEntity(null);
      // Refresh entity lists
      fetchAllEntities();
    } catch (err) {
      setError('Delete failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDeceased = async () => {
    if (!selectedEntity) {
      setError('Please select a character to mark as deceased');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(getApiUrl('/admin/mark-deceased'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          displayName: selectedEntity.name
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to mark character as deceased');
      }

      const result = await response.json();
      setSuccess(result.message);
      setMarkDeceasedDialog(false);
      setSelectedEntity(null);
      // Refresh entity lists
      fetchAllEntities();
    } catch (err) {
      setError('Failed to mark as deceased: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEntity = async () => {
    if (!selectedEntity || Object.keys(editEntityData).length === 0) {
      setError('Please select an entity and fill in at least one field to edit');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const entityType = managementTab === 0 ? 'character' : managementTab === 1 ? 'bank' : 'nation';
      const response = await fetch(getApiUrl(`/admin/${entityType}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify(editEntityData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Edit failed');
      }

      const result = await response.json();
      setSuccess(result.message);
      setEditDialog(false);
      setEditEntityData({});
      setSelectedEntity(null);
      // Refresh entity lists
      fetchAllEntities();
    } catch (err) {
      setError('Edit failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOfficerAction = async () => {
    setLoading(true);
    setError('');
    
    try {
      const endpoint = officerAction === 'promote' ? '/admin/promote-officer' : '/admin/demote-officer';
      
      const payload = {
        characterName: officerCharacterName,
        entityType: officerEntityType,
        entityId: officerEntityId
      };
      
      const response = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to ${officerAction} officer`);
      }
      
      const data = await response.json();
      setSuccess(data.message);
      setOfficerDialog(false);
      setOfficerCharacterName('');
      setOfficerEntityId('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const openOfficerManagementDialog = async (entity) => {
    setSelectedEntityForOfficers(entity);
    setEntityOfficers([]);
    setEntityMembers([]);
    setOfficerManagementDialog(true);
    
    try {
      setLoading(true);
      
      if (entity.type === 'bank') {
        // Fetch bank details to get officers
        const response = await fetch(getApiUrl(`/banks/${entity.id}`), {
          headers: {
            'Authorization': `Bearer ${sessionData.token}`,
          },
        });
        
        if (response.ok) {
          const bankData = await response.json();
          setEntityOfficers(bankData.officers || []);
        }
      } else if (entity.type === 'nation') {
        // Fetch nation details to get officers and members
        const response = await fetch(getApiUrl(`/nations/${entity.id}`), {
          headers: {
            'Authorization': `Bearer ${sessionData.token}`,
          },
        });
        
        if (response.ok) {
          const nationData = await response.json();
          setEntityOfficers(nationData.officers || []);
          setEntityMembers(nationData.citizens || []);
        }
      }
    } catch (err) {
      setError('Failed to load entity details');
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteToOfficer = async (characterName) => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/admin/promote-officer'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          characterName,
          entityType: selectedEntityForOfficers.type,
          entityId: selectedEntityForOfficers.type === 'bank' ? selectedEntityForOfficers.id : selectedEntityForOfficers.name
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to promote officer');
      }

      const data = await response.json();
      setSuccess(data.message);
      
      // Refresh the officer list
      await openOfficerManagementDialog(selectedEntityForOfficers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoteOfficer = async (characterName) => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/admin/demote-officer'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          characterName,
          entityType: selectedEntityForOfficers.type,
          entityId: selectedEntityForOfficers.type === 'bank' ? selectedEntityForOfficers.id : selectedEntityForOfficers.name
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to demote officer');
      }

      const data = await response.json();
      setSuccess(data.message);
      
      // Refresh the officer list
      await openOfficerManagementDialog(selectedEntityForOfficers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCitizen = async (characterName) => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/admin/remove-citizen'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          characterName,
          nationId: selectedEntityForOfficers.name
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove citizen');
      }

      const data = await response.json();
      setSuccess(data.message);
      
      // Refresh the officer list
      await openOfficerManagementDialog(selectedEntityForOfficers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (entity, action) => {
    setSelectedEntity(entity);
    setActionType(action);
    
    if (action === 'delete') {
      setDeleteDialog(true);
    } else if (action === 'edit') {
      // Pre-fill edit data based on entity type
      if (managementTab === 0) { // Character
        setEditEntityData({
          displayName: entity.name, // Used for identification
          newName: entity.name, // Editable name field
          profilePictureUrl: entity.profilePictureUrl || '',
          biography: entity.biography || ''
        });
      } else if (managementTab === 1) { // Bank
        setEditEntityData({
          bankId: entity.id, // Read-only, used for identification
          name: entity.name || '',
          abbreviation: entity.abbreviation || ''
        });
      } else if (managementTab === 2) { // Nation
        setEditEntityData({
          nationId: entity.id, // Read-only, used for identification
          name: entity.name || '',
          abbreviation: entity.abbreviation || '',
          description: entity.description || ''
        });
      }
      setEditDialog(true);
    }
  };

  const getCurrentEntities = () => {
    const entities = managementTab === 0 ? characters : managementTab === 1 ? banks : nations;
    return entities.filter(entity => {
      const searchLower = searchTerm.toLowerCase();
      return entity.name.toLowerCase().includes(searchLower) ||
             (entity.abbreviation && entity.abbreviation.toLowerCase().includes(searchLower)) ||
             (entity.jurisdictionNation && entity.jurisdictionNation.toLowerCase().includes(searchLower));
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleDateString();
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const then = new Date(timestamp);
    const diffDays = Math.floor((now - then) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const renderEntityCard = (entity) => {
    const isCharacter = managementTab === 0;
    const isBank = managementTab === 1;
    const isNation = managementTab === 2;

    return (
      <Grid item xs={12} sm={6} md={4} key={entity.id}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {isCharacter ? (
                <CharacterAvatar
                  displayName={entity.name}
                  profilePictureUrl={entity.profilePictureUrl}
                  size={60}
                  sx={{ mr: 2 }}
                  isAdmin={true}
                  isDeceased={entity.isDeceased || false}
                />
              ) : isNation ? (
                <Avatar
                  src={getImageUrl(entity.profilePictureUrl, true)}
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    bgcolor: 'secondary.main',
                    mr: 2
                  }}
                >
                  <Flag sx={{ color: 'white' }} />
                </Avatar>
              ) : (
                <Box sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <AccountBalance sx={{ color: 'white' }} />
                </Box>
              )}
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {entity.name}
                </Typography>
                {entity.abbreviation && (
                  <Typography variant="body2" color="text.secondary">
                    {entity.abbreviation}
                  </Typography>
                )}
              </Box>
            </Box>

            {isCharacter && (
              <>
                {entity.isDeceased && (
                  <Chip
                    icon={<span style={{ fontSize: 14 }}>💀</span>}
                    label="Deceased"
                    color="error"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                )}
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  First used: {formatDate(entity.firstUsed)}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Last seen: {getTimeSince(entity.lastSeen)}
                </Typography>
                {entity.biography && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {entity.biography.length > 100 ? `${entity.biography.substring(0, 100)}...` : entity.biography}
                  </Typography>
                )}
              </>
            )}

            {isBank && (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Jurisdiction: {entity.jurisdictionNation}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Created: {formatDate(entity.createdAt)}
                </Typography>
              </>
            )}

            {isNation && (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Created: {formatDate(entity.createdAt)}
                </Typography>
                {entity.nationalCurrency && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Currency: {entity.nationalCurrency}
                  </Typography>
                )}
                {entity.description && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {entity.description.substring(0, 100)}...
                  </Typography>
                )}
              </>
            )}
          </CardContent>
          
          <Divider />
          
          <CardActions sx={{ justifyContent: 'space-between', p: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => openActionDialog(entity, 'edit')}
                disabled={loading}
              >
                Edit
              </Button>
              {isCharacter && !entity.isDeceased && (
                <Button
                  size="small"
                  startIcon={<span style={{ fontSize: 16 }}>💀</span>}
                  color="warning"
                  onClick={() => {
                    setSelectedEntity(entity);
                    setMarkDeceasedDialog(true);
                  }}
                  disabled={loading}
                >
                  Mark Deceased
                </Button>
              )}
              {(isBank || isNation) && (
                <Button
                  size="small"
                  startIcon={<AdminPanelSettings />}
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    openOfficerManagementDialog({ ...entity, type: isBank ? 'bank' : 'nation' });
                  }}
                  disabled={loading}
                >
                  Officers
                </Button>
              )}
            </Box>
            <Button
              size="small"
              startIcon={<Delete />}
              color="error"
              onClick={() => openActionDialog(entity, 'delete')}
              disabled={loading}
            >
              Delete
            </Button>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  const renderTransferTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Admin Transfers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Perform transfers as any character. Admin transfers bypass balance checks and limits.
          </Typography>
          
          <Button
            variant="contained"
            startIcon={<SwapHoriz />}
            onClick={() => setTransferDialog(true)}
            disabled={loading}
          >
            Initiate Admin Transfer
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderSystemSettingsTab = () => {
    return (
      <Box>
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            System Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage system-wide configuration options.
          </Typography>

          {/* UGC Controls */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    User-Generated Content (UGC)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Control whether user-uploaded profile pictures and nation icons are displayed.
                    When disabled, the system will show placeholder images instead.
                    Admins can always see UGC regardless of this setting.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip 
                    label={ugcEnabled ? "Enabled" : "Disabled"}
                    color={ugcEnabled ? "success" : "error"}
                    variant="outlined"
                  />
                  <Button
                    variant="contained"
                    color={ugcEnabled ? "error" : "success"}
                    onClick={() => toggleUgc(!ugcEnabled)}
                    disabled={ugcLoading}
                    startIcon={ugcLoading ? <CircularProgress size={20} /> : (ugcEnabled ? <VisibilityOff /> : <Visibility />)}
                  >
                    {ugcLoading ? 'Processing...' : (ugcEnabled ? 'Disable UGC' : 'Enable UGC')}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Box>
    );
  };

  const renderManagementTab = () => {
    const currentEntities = getCurrentEntities();
    const entityTypeName = managementTab === 0 ? 'Characters' : managementTab === 1 ? 'Banks' : 'Nations';
    const entityIcon = managementTab === 0 ? <Person /> : managementTab === 1 ? <AccountBalance /> : <Flag />;

    return (
      <Box>
        <Paper elevation={3} sx={{ mb: 3 }}>
          <Tabs value={managementTab} onChange={handleManagementTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<Person />} label="Characters" />
            <Tab icon={<AccountBalance />} label="Banks" />
            <Tab icon={<Flag />} label="Nations" />
          </Tabs>
        </Paper>

        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {entityTypeName} Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Search and manage {entityTypeName.toLowerCase()}. Use Edit/Delete buttons for basic actions, or click "Officers" on banks and nations to manage their officer positions.
          </Typography>
          
          <TextField
            fullWidth
            variant="outlined"
            placeholder={`Search ${entityTypeName.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {entitiesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {currentEntities.map(renderEntityCard)}
          </Grid>
        )}

        {!entitiesLoading && currentEntities.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {entityIcon}
            <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
              {searchTerm ? `No ${entityTypeName.toLowerCase()} found matching your search` : `No ${entityTypeName.toLowerCase()} found`}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };



  const renderEditDialog = () => {
    const entityType = managementTab === 0 ? 'character' : managementTab === 1 ? 'bank' : 'nation';
    
    switch (entityType) {
      case 'character':
        return (
          <>
            <TextField
              fullWidth
              label="Display Name"
              value={editEntityData.newName || editEntityData.displayName || ''}
              onChange={(e) => setEditEntityData(prev => ({ ...prev, newName: e.target.value }))}
              margin="normal"
              helperText="Edit the character's display name"
            />
            <TextField
              fullWidth
              label="Profile Picture URL"
              value={editEntityData.profilePictureUrl || ''}
              onChange={(e) => setEditEntityData(prev => ({ ...prev, profilePictureUrl: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Biography"
              value={editEntityData.biography || ''}
              onChange={(e) => setEditEntityData(prev => ({ ...prev, biography: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
            />
          </>
        );
      case 'bank':
        return (
          <>
            <TextField
              fullWidth
              label="Bank ID"
              value={editEntityData.bankId || ''}
              margin="normal"
              disabled
              helperText="Bank IDs cannot be changed (referenced by accounts and transactions)"
            />
            <TextField
              fullWidth
              label="Name"
              value={editEntityData.name || ''}
              onChange={(e) => setEditEntityData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Abbreviation"
              value={editEntityData.abbreviation || ''}
              onChange={(e) => setEditEntityData(prev => ({ ...prev, abbreviation: e.target.value }))}
              margin="normal"
            />
            <GlassInfoCard severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Jurisdiction nation cannot be changed as it affects regulatory compliance and existing accounts.
              </Typography>
            </GlassInfoCard>
          </>
        );
      case 'nation':
        return (
          <>
            <TextField
              fullWidth
              label="Nation Name"
              value={editEntityData.name || ''}
              onChange={(e) => setEditEntityData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              helperText="Edit the nation's name"
            />
            <TextField
              fullWidth
              label="Nation Abbreviation"
              value={editEntityData.abbreviation || ''}
              onChange={(e) => setEditEntityData(prev => ({ ...prev, abbreviation: e.target.value }))}
              margin="normal"
              helperText="Edit the nation's abbreviation"
            />
            <TextField
              fullWidth
              label="Description"
              value={editEntityData.description || ''}
              onChange={(e) => setEditEntityData(prev => ({ ...prev, description: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ background: 'linear-gradient(45deg, #ff6b6b 30%, #ee5a24 90%)' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Security sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Divine Entity Admin Panel
          </Typography>
          <Chip
            label="ADMIN"
            color="error"
            variant="filled"
            sx={{ fontWeight: 'bold' }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && (
          <GlassInfoCard severity="error" sx={{ mb: 3 }}>
            {error}
          </GlassInfoCard>
        )}

        {success && (
          <GlassInfoCard severity="success" sx={{ mb: 3 }}>
            {success}
          </GlassInfoCard>
        )}

        <Paper elevation={3} sx={{ mb: 4 }}>
          <Tabs value={currentTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Admin Transfers" />
            <Tab label="Entity Management" />
            <Tab label="System Settings" />
          </Tabs>
        </Paper>

        {currentTab === 0 && renderTransferTab()}
        {currentTab === 1 && renderManagementTab()}
        {currentTab === 2 && renderSystemSettingsTab()}

        {/* Admin Transfer Dialog */}
        <TransferDialog
          open={transferDialog}
          onClose={() => setTransferDialog(false)}
          onTransfer={handleAdminTransfer}
          availableCurrencies={currencies}
          userDisplayName={sessionData?.selectedDisplayName}
          sessionData={sessionData}
          isAdminMode={true}
        />

        {/* Delete Entity Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="error" />
              Delete {selectedEntity?.name}
            </Box>
          </DialogTitle>
          <DialogContent>
            <GlassWarningCard severity="error" sx={{ mb: 3 }}>
              This action cannot be undone. The entity "{selectedEntity?.name}" and all associated data will be permanently deleted.
            </GlassWarningCard>

            <Typography variant="body1">
              Are you sure you want to delete <strong>{selectedEntity?.name}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteEntity} 
              variant="contained" 
              color="error"
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Mark Deceased Dialog */}
        <Dialog open={markDeceasedDialog} onClose={() => setMarkDeceasedDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: 24 }}>💀</span>
              Mark {selectedEntity?.name} as Deceased
            </Box>
          </DialogTitle>
          <DialogContent>
            <GlassWarningCard severity="warning" sx={{ mb: 3 }}>
              This will mark the character "{selectedEntity?.name}" as deceased. They will appear last in lists and display with a deceased indicator.
            </GlassWarningCard>
            
            <Typography variant="body1">
              Are you sure you want to mark <strong>{selectedEntity?.name}</strong> as deceased?
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This action can only be reversed through direct database access.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMarkDeceasedDialog(false)} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkDeceased} 
              variant="contained" 
              color="warning"
              disabled={loading}
              startIcon={<span style={{ fontSize: 16 }}>💀</span>}
            >
              {loading ? 'Marking as Deceased...' : 'Mark as Deceased'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Entity Dialog */}
        <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit {selectedEntity?.name}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Update the entity information. Leave fields empty to keep current values.
            </Typography>
            
            {renderEditDialog()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditEntity} 
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Officer Management Dialog (Simple) */}
        <Dialog open={officerDialog} onClose={() => setOfficerDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {officerAction === 'promote' ? 'Promote Officer' : 'Demote Officer'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {officerAction === 'promote' 
                ? 'Grant officer privileges to a character in a bank or nation.'
                : 'Remove officer privileges from a character in a bank or nation.'
              }
            </Typography>
            
            <TextField
              fullWidth
              label="Character Name"
              value={officerCharacterName}
              onChange={(e) => setOfficerCharacterName(e.target.value)}
              margin="normal"
              helperText="Enter the exact display name of the character"
            />

            <TextField
              fullWidth
              select
              label="Entity Type"
              value={officerEntityType}
              onChange={(e) => setOfficerEntityType(e.target.value)}
              margin="normal"
              SelectProps={{
                native: true,
              }}
            >
              <option value="bank">Bank</option>
              <option value="nation">Nation</option>
            </TextField>

            <TextField
              fullWidth
              label={officerEntityType === 'bank' ? 'Bank ID' : 'Nation Name/Abbreviation'}
              value={officerEntityId}
              onChange={(e) => setOfficerEntityId(e.target.value)}
              margin="normal"
              helperText={
                officerEntityType === 'bank' 
                  ? 'Enter the bank ID (e.g., "BANK_001")'
                  : 'Enter the nation name or abbreviation'
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOfficerDialog(false)} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleOfficerAction} 
              variant="contained"
              color={officerAction === 'promote' ? 'success' : 'error'}
              disabled={loading || !officerCharacterName || !officerEntityId}
            >
              {loading ? 'Processing...' : (officerAction === 'promote' ? 'Promote' : 'Demote')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Detailed Officer Management Dialog */}
        <Dialog 
          open={officerManagementDialog} 
          onClose={() => setOfficerManagementDialog(false)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '50%', 
                bgcolor: selectedEntityForOfficers?.type === 'bank' ? 'primary.main' : 'secondary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {selectedEntityForOfficers?.type === 'bank' ? 
                  <AccountBalance sx={{ color: 'white' }} /> : 
                  <Flag sx={{ color: 'white' }} />
                }
              </Box>
              <Box>
                <Typography variant="h6">
                  {selectedEntityForOfficers?.name} Officers
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedEntityForOfficers?.type === 'bank' ? 'Bank' : 'Nation'} • {selectedEntityForOfficers?.abbreviation}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {/* Officers Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Current Officers ({entityOfficers.length})
              </Typography>
              
              {entityOfficers.length > 0 ? (
                <List>
                  {entityOfficers.map((officer) => (
                                         <ListItem
                       key={officer.name}
                       secondaryAction={
                         <Box sx={{ display: 'flex', gap: 1 }}>
                           <Button
                             size="small"
                             color="warning"
                             onClick={() => handleDemoteOfficer(officer.name)}
                             disabled={loading}
                           >
                             Demote
                           </Button>
                           {selectedEntityForOfficers?.type === 'nation' && (
                             <Button
                               size="small"
                               color="error"
                               onClick={() => handleRemoveCitizen(officer.name)}
                               disabled={loading}
                             >
                               Remove
                             </Button>
                           )}
                         </Box>
                       }
                     >
                      <ListItemAvatar>
                        <CharacterAvatar
                          profilePictureUrl={officer.profilePictureUrl}
                          displayName={officer.name}
                          size={40}
                          sx={{ bgcolor: 'primary.main' }}
                          isAdmin={true}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={officer.name}
                        secondary={`Officer since ${formatDate(officer.joinDate || officer.joinedAt)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <GlassInfoCard severity="info">No officers found</GlassInfoCard>
              )}
            </Box>

            {/* Members Section (for nations only) */}
            {selectedEntityForOfficers?.type === 'nation' && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Citizens Available for Promotion ({entityMembers.filter(member => 
                    !entityOfficers.some(officer => officer.name === member.name)
                  ).length})
                </Typography>
                
                {entityMembers.filter(member => 
                  !entityOfficers.some(officer => officer.name === member.name)
                ).length > 0 ? (
                  <List>
                    {entityMembers
                      .filter(member => !entityOfficers.some(officer => officer.name === member.name))
                      .map((member) => (
                                                 <ListItem
                           key={member.name}
                           secondaryAction={
                             <Box sx={{ display: 'flex', gap: 1 }}>
                               <Button
                                 size="small"
                                 color="success"
                                 onClick={() => handlePromoteToOfficer(member.name)}
                                 disabled={loading}
                               >
                                 Promote
                               </Button>
                               <Button
                                 size="small"
                                 color="error"
                                 onClick={() => handleRemoveCitizen(member.name)}
                                 disabled={loading}
                               >
                                 Remove
                               </Button>
                             </Box>
                           }
                         >
                          <ListItemAvatar>
                            <CharacterAvatar
                              profilePictureUrl={member.profilePictureUrl}
                              displayName={member.name}
                              size={40}
                              sx={{ bgcolor: 'secondary.main' }}
                              isAdmin={true}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={member.name}
                            secondary={`Citizen since ${formatDate(member.joinDate || member.joinedAt)}`}
                          />
                        </ListItem>
                      ))}
                  </List>
                ) : (
                  <GlassInfoCard severity="info">
                    All citizens are already officers, or no citizens available for promotion
                  </GlassInfoCard>
                )}
              </Box>
            )}

            {/* Manual Promotion Section */}
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>
                Manual Officer Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Promote any character to officer status (they don't need to be a member first for banks).
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="Character Name"
                  value={officerCharacterName}
                  onChange={(e) => setOfficerCharacterName(e.target.value)}
                  placeholder="Enter character display name"
                  size="small"
                />
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handlePromoteToOfficer(officerCharacterName)}
                  disabled={loading || !officerCharacterName.trim()}
                  sx={{ minWidth: 100 }}
                >
                  Promote
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOfficerManagementDialog(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminPanel; 